// ============================================================
// TrialRequestDetailPage.js
// Route /trial-requests/:trialRequestId — single trial request with
// all submitted fields, status meta, inline-editable notes, and
// status-driven action buttons (provision / extend / convert /
// decline).
//
// Detail-as-separate-page, matching TenantDetailPage / ClientDetailPage.
// Inline 404 handling. Notes save on blur via PATCH.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import ConfirmDialog from '../components/ConfirmDialog';
import { TrialStatusBadge } from '../components/StatusBadge';
import ProvisionTrialDialog from './ProvisionTrialDialog';
import {
  getAdminTrialRequest,
  extendAdminTrialRequest,
  declineAdminTrialRequest,
  updateAdminTrialRequestNotes,
} from '../api/adminTrialRequests';
import { convertToPaid } from '../api/bcBilling';
import { translateError } from '../utils/errorMessages';
import { formatDate } from '../utils/format';
import { toast } from '../components/Toaster';
import StatusMessage from '../components/StatusMessage';
import './TrialRequestDetailPage.css';

const ACTIVE_TRIAL_STATUSES = new Set(['Provisioned', 'Extended']);

function daysRemaining(trialExpiryDate) {
  if (!trialExpiryDate) return null;
  const expiry = new Date(trialExpiryDate);
  if (Number.isNaN(expiry.getTime())) return null;
  return Math.ceil((expiry.getTime() - Date.now()) / (24 * 60 * 60 * 1000));
}

export default function TrialRequestDetailPage() {
  const { trialRequestId: idParam } = useParams();
  const trialRequestId = parseInt(idParam, 10);
  const isValidId = Number.isInteger(trialRequestId) && trialRequestId > 0;

  const [trial, setTrial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);

  // Notes inline edit.
  const [notes, setNotes] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);

  // Action state.
  const [actionBusy, setActionBusy] = useState(false);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // { kind: 'extend'|'convert'|'decline' }

  const fetchDetail = useCallback(async () => {
    if (!isValidId) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setNotFound(false);
    try {
      const data = await getAdminTrialRequest(trialRequestId);
      setTrial(data);
      setNotes(data.notes || '');
    } catch (err) {
      if (err && err.status === 404) setNotFound(true);
      else setError(translateError(err));
    } finally {
      setLoading(false);
    }
  }, [trialRequestId, isValidId]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  function applyUpdated(updated) {
    setTrial(updated);
    setNotes(updated.notes || '');
  }

  async function handleNotesBlur() {
    if (!trial) return;
    const current = trial.notes || '';
    if (notes === current) return;
    setNotesSaving(true);
    try {
      const updated = await updateAdminTrialRequestNotes(trialRequestId, notes);
      applyUpdated(updated);
      toast.success('Notes saved');
    } catch (err) {
      setNotes(current); // revert on failure
      toast.error(translateError(err));
    } finally {
      setNotesSaving(false);
    }
  }

  async function runConfirmAction() {
    if (!confirm) return;
    setActionBusy(true);
    try {
      let updated;
      if (confirm.kind === 'extend') updated = await extendAdminTrialRequest(trialRequestId);
      else if (confirm.kind === 'convert') {
        // Convert to paid (2026-10-22): one endpoint by client, which also
        // starts the Sales and Business Central set-up where it applies.
        await convertToPaid(trial.clientId);
        updated = await getAdminTrialRequest(trialRequestId);
      }
      else if (confirm.kind === 'decline') updated = await declineAdminTrialRequest(trialRequestId);
      applyUpdated(updated);
      toast.success(
        confirm.kind === 'extend' ? 'Trial extended by 30 days'
          : confirm.kind === 'convert' ? 'Converted to paid. See the client Billing tab for the Business Central set-up.'
            : 'Trial request declined'
      );
      setConfirm(null);
    } catch (err) {
      toast.error(translateError(err));
    } finally {
      setActionBusy(false);
    }
  }

  if (loading) {
    return (
      <>
        <PageHeader title="Loading…" />
        <StatusMessage variant="loading" className="trial-detail__state">Loading trial request…</StatusMessage>
      </>
    );
  }

  if (notFound) {
    return (
      <>
        <PageHeader title="Trial request not found" />
        <StatusMessage variant="empty" className="trial-detail__state trial-detail__state--empty">
          <p>That trial request does not exist.</p>
          <Link to="/trial-requests" className="trial-detail__back-link">← Return to Trial Requests</Link>
        </StatusMessage>
      </>
    );
  }

  if (error) {
    return (
      <>
        <PageHeader title="Trial request" />
        <StatusMessage variant="error" className="trial-detail__state trial-detail__state--error">
          <p>{error}</p>
          <button type="button" className="trial-detail__retry" onClick={fetchDetail}>Retry</button>
        </StatusMessage>
      </>
    );
  }

  const fullName = `${trial.firstName} ${trial.lastName}`.trim();
  const days = daysRemaining(trial.trialExpiryDate);
  // 2026-10-09: while the trial waits for its first sign-in, the expiry date
  // is the 90-day waiting deadline, so it is shown as that, not as days left.
  const waiting = trial.trialAwaitingFirstSignIn === true;
  const showDays = days != null && !waiting && ACTIVE_TRIAL_STATUSES.has(trial.status);
  const isActiveTrial = ACTIVE_TRIAL_STATUSES.has(trial.status);
  const isNew = trial.status === 'New';
  const isTerminal = trial.status === 'Converted' || trial.status === 'Declined';

  return (
    <>
      <PageHeader
        title={trial.organisationName}
        breadcrumb={<Link to="/trial-requests" className="trial-detail__back-link">← Trial Requests</Link>}
      />

      <div className="trial-detail__meta">
        <TrialStatusBadge status={trial.status} />
        <span className="trial-detail__meta-item">
          {waiting ? 'Approved' : 'Trial start'}: <strong>{trial.trialStartDate ? formatDate(trial.trialStartDate) : '—'}</strong>
        </span>
        {waiting ? (
          <span className="trial-detail__meta-item" data-testid="trial-waiting">
            Waiting for first sign-in. Closes on <strong>{formatDate(trial.trialExpiryDate)}</strong> if nobody signs in.
          </span>
        ) : (
          <span className="trial-detail__meta-item">
            Trial expiry: <strong>{trial.trialExpiryDate ? formatDate(trial.trialExpiryDate) : '—'}</strong>
          </span>
        )}
        {showDays && (
          <span
            className="trial-detail__meta-item"
            style={days <= 7 ? { color: 'var(--color-error)', fontWeight: 600 } : undefined}
          >
            {days} {days === 1 ? 'day' : 'days'} remaining
          </span>
        )}
      </div>

      <div className="trial-detail__grid">
        {/* Submitted details */}
        <section className="trial-detail__card">
          <h2 className="trial-detail__card-title">Submitted details</h2>
          <dl className="trial-detail__fields">
            <div><dt>Name</dt><dd>{fullName}</dd></div>
            <div><dt>Email</dt><dd><a href={`mailto:${trial.email}`}>{trial.email}</a></dd></div>
            <div><dt>Organisation</dt><dd>{trial.organisationName}</dd></div>
            <div><dt>Role</dt><dd>{trial.role}</dd></div>
            <div><dt>Phone</dt><dd><a href={`tel:${trial.phone}`}>{trial.phone}</a></dd></div>
            <div><dt>Submitted</dt><dd>{formatDate(trial.submittedAt)}</dd></div>
            {/* Slice C: lead-level attribution — only shown when captured. */}
            {(trial.utmSource || trial.utmCampaign || trial.utmContent) && (
              <div>
                <dt>Source</dt>
                <dd>
                  {[trial.utmSource, trial.utmMedium, trial.utmCampaign, trial.utmContent]
                    .filter(Boolean)
                    .join(' · ')}
                </dd>
              </div>
            )}
            {trial.tenantId != null && (
              <div>
                <dt>Tenant</dt>
                <dd><Link to={`/tenants/${trial.tenantId}`}>{trial.tenantName || `Tenant #${trial.tenantId}`}</Link></dd>
              </div>
            )}
            {trial.clientId != null && (
              <div>
                <dt>Client</dt>
                <dd><Link to={`/clients/${trial.clientId}`}>{trial.clientName || `Client #${trial.clientId}`}</Link></dd>
              </div>
            )}
          </dl>
        </section>

        {/* Notes */}
        <section className="trial-detail__card">
          <h2 className="trial-detail__card-title">
            Notes {notesSaving && <span className="trial-detail__saving">saving…</span>}
          </h2>
          <textarea
            className="trial-detail__notes"
            value={notes}
            placeholder="Internal notes — saved when you click away."
            maxLength={4000}
            onChange={(e) => setNotes(e.target.value)}
            onBlur={handleNotesBlur}
            disabled={notesSaving}
          />
        </section>
      </div>

      {/* Actions */}
      {!isTerminal && (
        <div className="trial-detail__actions">
          {isNew && (
            <button
              type="button"
              className="trial-detail__btn trial-detail__btn--primary"
              onClick={() => setProvisionOpen(true)}
              disabled={actionBusy}
            >
              Provision Trial
            </button>
          )}
          {isActiveTrial && (
            <>
              <button
                type="button"
                className="trial-detail__btn trial-detail__btn--primary"
                onClick={() => setConfirm({ kind: 'extend' })}
                disabled={actionBusy}
              >
                Extend Trial
              </button>
              <button
                type="button"
                className="trial-detail__btn trial-detail__btn--success"
                onClick={() => setConfirm({ kind: 'convert' })}
                disabled={actionBusy || trial.clientId == null}
              >
                Convert to paid
              </button>
            </>
          )}
          <button
            type="button"
            className="trial-detail__btn trial-detail__btn--danger"
            onClick={() => setConfirm({ kind: 'decline' })}
            disabled={actionBusy}
          >
            Decline
          </button>
        </div>
      )}

      <ProvisionTrialDialog
        isOpen={provisionOpen}
        onClose={() => setProvisionOpen(false)}
        trial={trial}
        onProvisioned={(updated) => { applyUpdated(updated); setProvisionOpen(false); }}
      />

      <ConfirmDialog
        isOpen={confirm?.kind === 'extend'}
        onClose={() => setConfirm(null)}
        onConfirm={runConfirmAction}
        title="Extend Trial"
        message={`Extend trial for ${trial.organisationName} by 30 days?`}
        confirmLabel="Extend"
        tone="primary"
        isSubmitting={actionBusy}
      />
      <ConfirmDialog
        isOpen={confirm?.kind === 'convert'}
        onClose={() => setConfirm(null)}
        onConfirm={runConfirmAction}
        title="Convert to paid"
        message={`Convert ${trial.organisationName} to a paying client? This cannot be undone.`}
        confirmLabel="Convert to paid"
        tone="primary"
        isSubmitting={actionBusy}
      />
      <ConfirmDialog
        isOpen={confirm?.kind === 'decline'}
        onClose={() => setConfirm(null)}
        onConfirm={runConfirmAction}
        title="Decline trial request"
        message="Decline this trial request? This cannot be undone."
        confirmLabel="Decline"
        tone="danger"
        isSubmitting={actionBusy}
      />
    </>
  );
}
